<template>
  <div class="address-fields-input">
    <b-row>
      <b-col>
        <b-form-group>
          <custom-input
            ref="address_1"
            v-model="form.address_1"
            type="search"
            placeholder="Address Line 1"
            autocomplete="off"
            required
            :disabled="form.$busy || this.isPopulating"
            :is-invalid="form.$getError('address_1')"
            @keydown.esc="select(-1)"
            @blur="hide"
            @focus="show"
          ></custom-input>
          <error-message :error="form.$getError('address_1')"></error-message>

          <b-dropdown
            variant="primary"
            :class="[
              'dropdown--primary',
              {
                hide: !showPredictions
              }
            ]"
            :menu-class="{
              show: showPredictions
            }"
            no-caret
          >
            <b-dropdown-item
              v-for="(item, index) in predictions"
              :key="item.id"
              :item="item"
              @click="select(index)"
              @keydown.esc="select(-1)"
            >
              {{ item.description }}
            </b-dropdown-item>
          </b-dropdown>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group>
          <custom-input
            ref="locality"
            v-model="form.city"
            type="text"
            autocomplete="off"
            placeholder="City or Suburb"
            required
            :disabled="form.$busy || this.isPopulating"
            :is-invalid="form.$getError('city')"
            @focus="select(-1)"
          ></custom-input>
          <error-message :error="form.$getError('city')"></error-message>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group>
          <custom-input
            ref="postal_code"
            v-model="form.postal_code"
            type="number"
            autocomplete="off"
            placeholder="Postcode"
            required
            :disabled="form.$busy || this.isPopulating"
            :is-invalid="form.$getError('postal_code')"
            @focus="select(-1)"
          ></custom-input>
          <error-message :error="form.$getError('postal_code')"></error-message>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group>
          <custom-input
            ref="administrative_area_level_1"
            v-model="form.state"
            type="text"
            autocomplete="off"
            placeholder="State"
            required
            :disabled="form.$busy || this.isPopulating"
            :is-invalid="form.$getError('state')"
            @focus="select(-1)"
          ></custom-input>
          <error-message :error="form.$getError('state')"></error-message>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group>
          <custom-input
            ref="country"
            v-model="form.country"
            type="text"
            autocomplete="off"
            placeholder="Country"
            required
            :disabled="form.$busy || this.isPopulating"
            :is-invalid="form.$getError('country')"
            @focus="select(-1)"
          ></custom-input>
          <error-message :error="form.$getError('country')"></error-message>
        </b-form-group>
      </b-col>
    </b-row>

    <custom-input
      ref="latitude"
      v-model="form.latitude"
      class="hidden-field"
      tabindex="-1"
    />
    <custom-input
      ref="longitude"
      v-model="form.longitude"
      class="hidden-field"
      tabindex="-1"
    />
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Autocomplete of all address fields
 * ==================================================================================
 **/

import geocode from './Geocode'
import Form from '@/utils/form'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'

const ADDRESS_FIELDS = [
  'address_1',
  'city',
  'postal_code',
  'state',
  'country',
  'latitude',
  'longitude'
]

const COMPONENT_FORM = {
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'short_name',
  country: 'long_name',
  postal_code: 'short_name'
}

export default {
  components: {
    CustomInput,
    ErrorMessage
  },
  props: {
    form: {
      type: [Object, Form],
      validator(obj) {
        if (typeof obj === 'object' && obj !== null) {
          for (const str of ADDRESS_FIELDS) {
            if (!obj.hasOwnProperty(str)) {
              return false
            }
          }

          return true
        }

        return false
      }
    },

    /**
     * Google places API properties
     */
    baseUri: {
      type: String,
      default: 'https://maps.googleapis.com/maps/api/js'
    },

    apiKey: {
      type: String
    },

    libraries: {
      type: Array,
      default() {
        return ['geometry', 'places']
      }
    }
  },
  computed: {
    /**
     * Full address search string
     */
    place() {
      let address = [this.form.address_1]
      if (this.form.city) address.push(this.form.city)
      if (this.form.state) address.push(this.form.state)
      if (this.form.country) address.push(this.form.country)
      return address.join(',')
    },

    googlePlacesUrl() {
      return `${this.baseUri}?key=${
        this.apiKey
      }&libraries=${this.libraries.join(',')}`
    },

    hasGooglePlaces() {
      return !!this.$data.$geocoder && !!this.$data.$service
    },

    hasCoordinates() {
      return this.form ? this.form.latitude && this.form.longitude : false
    },

    showPredictions() {
      return this.hasFocus && this.predictions && this.predictions.length > 0
    }
  },
  watch: {
    'form.address_1'(value) {
      if (value) {
        this.search()
      } else {
        this.setCoordinates(null)
      }
    }
  },
  data: function() {
    return {
      $geocoder: null,
      $service: null,

      predictions: [],

      isPopulating: false,
      hasInitialFetch: false,
      hasFocus: false
    }
  },
  methods: {
    initialFetch() {
      if (!this.hasInitialFetch && this.place && !this.hasCoordinates) {
        this.hasInitialFetch = true
        this.search(true)
      } else {
        this.setLoading(false)
      }
    },

    async search(autoFetch = false) {
      await this.getPredictions()
        .then(response => {
          this.predictions = response

          if (autoFetch && this.predictions.length > 0) {
            this.select(0, false, autoFetch)
          }
        })
        .catch(() => {
          this.predictions = []
          this.setLoading(false, autoFetch)
        })
    },

    select(index = null, updateFields = true, autoFetch = false) {
      if (index < 0) return

      let prediction = this.predictions[index]

      this.predictions = []
      this.setLoading(true, autoFetch)

      geocode({ placeId: prediction.place_id })
        .then(response => {
          let place = response[0]
          this.setCoordinates(place)

          if (updateFields) {
            this.setFields(place)
          }

          this.$nextTick(() => {
            this.setLoading(false, autoFetch)
          })
        })
        .catch(() => {
          this.$nextTick(() => {
            this.setLoading(false, autoFetch)
          })
        })
    },

    getPredictions() {
      return new Promise((resolve, reject) => {
        if (this.hasGooglePlaces && this.place && !this.isPopulating) {
          this.$data.$service.getPlacePredictions(
            {
              input: this.place,
              types: ['geocode']
            },
            (response, status) => {
              switch (status) {
                case window.google.maps.places.PlacesServiceStatus.OK:
                  resolve(response)
                  break
                default:
                  reject(new Error(`Error with status: ${status}`))
              }
            }
          )
        } else {
          reject(new Error('Google places API not initialized'))
        }
      })
    },

    setFields(place) {
      if (!place) return

      this.$refs.address_1.$emit('input', null)

      for (const component of place.address_components) {
        const addressType = component.types[0]
        if (COMPONENT_FORM[addressType]) {
          const val = component[COMPONENT_FORM[addressType]]

          switch (addressType) {
            case 'street_number':
            case 'route':
              this.$refs.address_1.$emit(
                'input',
                this.form.address_1 ? this.form.address_1 + ' ' + val : val
              )
              break
            case 'locality':
            case 'state':
            case 'administrative_area_level_1':
            case 'country':
            case 'postal_code':
              this.$refs[addressType].$emit('input', val)
              break
          }
        }
      }
    },

    setCoordinates(place) {
      if (place) {
        this.setLatitude(place.geometry.location.lat())
        this.setLongitude(place.geometry.location.lng())
      } else {
        this.setLatitude(null)
        this.setLongitude(null)
      }
    },

    setLatitude(lat) {
      this.$refs.latitude.$emit('input', lat)
    },

    setLongitude(lng) {
      this.$refs.longitude.$emit('input', lng)
    },

    setLoading(bool, emit = true) {
      this.isPopulating = bool

      /* Primarily use to check if the auto-fetch was successful */
      if (emit) {
        this.$emit('loading', bool)
      }
    },

    show() {
      this.hasFocus = true
    },

    hide() {
      setTimeout(() => {
        this.hasFocus = false
      }, 100)
    },

    loadPlacesScript() {
      return new Promise((resolve, reject) => {
        if (!this.isPlacesScriptLoaded()) {
          let script = document.createElement('script')
          script.setAttribute('src', this.googlePlacesUrl)
          script.async = true
          document.head.appendChild(script)

          script.onload = () => {
            resolve()
          }

          script.onerror = error => {
            reject(error)
          }
        } else {
          resolve()
        }
      })
    },

    isPlacesScriptLoaded() {
      return (
        typeof window.google === 'object' &&
        typeof window.google.maps === 'object' &&
        typeof window.google.maps.Geocoder === 'function' &&
        typeof window.google.maps.places === 'object' &&
        typeof window.google.maps.places.AutocompleteService === 'function'
      )
    }
  },
  mounted() {
    if (this.apiKey && !this.hasGooglePlaces) {
      this.loadPlacesScript()
        .then(() => {
          this.$data.$geocoder = new window.google.maps.Geocoder()
          this.$data.$service = new window.google.maps.places.AutocompleteService()

          /* Automatically fetch 1st suggestion */
          this.initialFetch()
        })
        .catch(() => {
          this.setLoading(false)
        })
    } else {
      this.setLoading(false)
    }
  }
}
</script>
<style lang="scss" scoped>
.address-fields-input /deep/ {
  .hidden-field {
    display: block;
    width: 0;
    height: 0;
    margin: 0;
    padding: 0;
    border: 0;
    outline: none;

    span {
      display: none;
    }
  }

  .dropdown {
    .dropdown-toggle {
      display: none;
    }

    .dropdown-menu {
      width: auto;
      padding: 1rem 0;
      box-shadow: var(--box-shadow);
    }
  }

  .dropdown.hide {
    display: none;
  }

  /* clears the ‘X’ from Internet Explorer */
  input[type='search']::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
  input[type='search']::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }
  /* clears the ‘X’ from Chrome */
  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button,
  input[type='search']::-webkit-search-results-button,
  input[type='search']::-webkit-search-results-decoration {
    display: none;
  }
}
</style>
